import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ImagePlus, Loader2, MessageCircle, Phone, X } from "lucide-react";
import { AREAS, CONTACTS, SERVICES, waLink } from "@/data/content";
import { Reveal, SectionHead } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCATIONS = [...AREAS.map((a) => a.name), "Outra localidade"];
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Outro / Vários serviços"];

const INITIAL = {
  nome: "",
  telefone: "",
  email: "",
  localidade: "",
  servico: "",
  tipo: "Serviço pontual",
  descricao: "",
  empresa: "",
};

const inputCls =
  "w-full rounded-xl border border-stone2 bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-moss/50 focus:border-lime-500 focus:ring-2 focus:ring-lime-200";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-moss";

export default function QuoteForm({ preselect }) {
  const [form, setForm] = useState(INITIAL);
  const [area, setArea] = useState(150);
  const [manualArea, setManualArea] = useState(null);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("idle");
  const fileInput = useRef(null);

  useEffect(() => {
    if (preselect) {
      const s = SERVICES.find((x) => x.slug === preselect);
      if (s) setForm((f) => ({ ...f, servico: s.title }));
    }
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const areaValue = manualArea !== null && manualArea ? `${manualArea} m²` : `${area} m²`;

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list)]
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, 5);
    setFiles(next);
  };

  const waMessage = () =>
    `Olá EcoJard! O meu nome é ${form.nome || "não indicado"}.\nServiço: ${
      form.servico || "não indicado"
    }\nLocalidade: ${form.localidade || "não indicada"}\nTipo: ${form.tipo}\nÁrea aproximada: ${areaValue}${
      form.descricao ? `\nDetalhes: ${form.descricao}` : ""
    }`;

  const submit = async (e) => {
    e.preventDefault();
    if (form.empresa) {
      setStatus("done");
      return;
    }
    setStatus("sending");
    try {
      const fd = new FormData();
      ["nome", "telefone", "email", "localidade", "servico", "tipo", "descricao"].forEach((k) =>
        fd.append(k, form[k])
      );
      fd.append("area", areaValue);
      files.forEach((f) => fd.append("fotos", f));
      await axios.post(`${API}/quote-requests`, fd);
      setStatus("done");
      toast.success("Pedido enviado com sucesso!");
    } catch {
      setStatus("idle");
      toast.error("Não foi possível enviar. Tente novamente ou contacte-nos por WhatsApp.");
    }
  };

  const reset = () => {
    setForm(INITIAL);
    setFiles([]);
    setArea(150);
    setManualArea(null);
    setStatus("idle");
  };

  return (
    <section id="orcamento" data-testid="quote-section" className="scroll-mt-24 bg-sand/60 py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <div>
          <SectionHead
            overline="Pedido de Orçamento"
            title="Conte-nos o que precisa. Nós tratamos do resto."
            text="Preencha o formulário e envie fotografias do espaço, se tiver. Entramos em contacto consigo para apresentar uma proposta gratuita e sem compromisso."
          />
          <Reveal delay={0.2}>
            <ol className="mt-10 flex flex-col gap-5">
              {[
                "Envie o pedido com os detalhes do espaço",
                "Falamos consigo por telefone ou WhatsApp",
                "Visita gratuita, se necessário, e proposta clara",
              ].map((step, i) => (
                <li key={step} className="flex items-center gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pine font-serif text-sm italic text-ivory">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-ink sm:text-base">{step}</span>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-10 flex flex-col gap-3">
              <a
                data-testid="quote-phone-link"
                href={CONTACTS.phoneHref}
                className="flex items-center gap-3 rounded-2xl border border-stone2 bg-white px-5 py-4 text-sm font-semibold text-pine transition-colors hover:border-lime-400"
              >
                <Phone size={18} className="text-lime-600" /> {CONTACTS.phoneDisplay}
              </a>
              <a
                data-testid="quote-phone2-link"
                href={CONTACTS.phone2Href}
                className="flex items-center gap-3 rounded-2xl border border-stone2 bg-white px-5 py-4 text-sm font-semibold text-pine transition-colors hover:border-lime-400"
              >
                <Phone size={18} className="text-lime-600" /> {CONTACTS.phone2Display}
              </a>
              <a
                data-testid="quote-whatsapp-link"
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl border border-stone2 bg-white px-5 py-4 text-sm font-semibold text-pine transition-colors hover:border-lime-400"
              >
                <MessageCircle size={18} className="text-[#25D366]" /> Falar no WhatsApp
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div className="rounded-3xl border border-stone2 bg-white p-6 shadow-[0_32px_70px_-30px_rgba(18,37,26,0.25)] sm:p-9">
            {status === "done" ? (
              <div data-testid="quote-form-success" className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-lime-100 text-lime-700">
                  <CheckCircle2 size={32} />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink">Pedido recebido!</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-moss">
                  Obrigado, {form.nome.split(" ")[0]}. Recebemos o seu pedido e vamos entrar em
                  contacto consigo. Para uma resposta ainda mais rápida, envie os mesmos dados por
                  WhatsApp.
                </p>
                <a
                  data-testid="quote-whatsapp-send-button"
                  href={waLink(waMessage())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#1EBE5A]"
                >
                  <MessageCircle size={17} /> Enviar também por WhatsApp
                </a>
                <button
                  data-testid="quote-new-request-button"
                  onClick={reset}
                  className="mt-4 text-sm font-medium text-moss underline-offset-4 hover:underline"
                >
                  Fazer novo pedido
                </button>
              </div>
            ) : (
              <form data-testid="quote-form" onSubmit={submit} className="flex flex-col gap-5">
                <input
                  type="text"
                  name="empresa"
                  value={form.empresa}
                  onChange={set("empresa")}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-nome" className={labelCls}>Nome *</label>
                    <input id="q-nome" data-testid="quote-form-name-input" required value={form.nome} onChange={set("nome")} placeholder="O seu nome" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="q-tel" className={labelCls}>Telefone *</label>
                    <input id="q-tel" data-testid="quote-form-phone-input" required type="tel" value={form.telefone} onChange={set("telefone")} placeholder="9XX XXX XXX" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="q-email" className={labelCls}>Email *</label>
                  <input id="q-email" data-testid="quote-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="oseuemail@exemplo.com" className={inputCls} />
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-loc" className={labelCls}>Localidade *</label>
                    <select id="q-loc" data-testid="quote-form-location-select" required value={form.localidade} onChange={set("localidade")} className={inputCls}>
                      <option value="" disabled>Selecionar…</option>
                      {LOCATIONS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="q-serv" className={labelCls}>Tipo de serviço *</label>
                    <select id="q-serv" data-testid="quote-form-service-select" required value={form.servico} onChange={set("servico")} className={inputCls}>
                      <option value="" disabled>Selecionar…</option>
                      {SERVICE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <span className={labelCls}>Pretende</span>
                  <div className="grid grid-cols-2 gap-3">
                    {["Serviço pontual", "Manutenção regular"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        data-testid={t === "Serviço pontual" ? "quote-form-type-oneoff" : "quote-form-type-regular"}
                        onClick={() => setForm((f) => ({ ...f, tipo: t }))}
                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                          form.tipo === t
                            ? "border-pine bg-pine text-ivory"
                            : "border-stone2 bg-white text-moss hover:border-lime-400"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor={manualArea === null ? "q-area" : "q-area-manual"} className={labelCls}>
                    Área aproximada:{" "}
                    <span className="text-pine">
                      {manualArea !== null ? `${manualArea || "…"} m²` : `${area} m²`}
                    </span>
                  </label>
                  {manualArea === null ? (
                    <>
                      <input
                        id="q-area"
                        data-testid="quote-form-area-slider"
                        type="range"
                        min={20}
                        max={5000}
                        step={10}
                        value={area}
                        onChange={(e) => setArea(Number(e.target.value))}
                        className="w-full accent-lime-600"
                      />
                      <div className="flex justify-between text-xs text-moss/70">
                        <span>20 m²</span>
                        <span>5000 m²</span>
                      </div>
                      <button
                        type="button"
                        data-testid="quote-form-area-manual-toggle"
                        onClick={() => setManualArea("")}
                        className="mt-2 text-xs font-semibold text-pine underline decoration-lime-500 decoration-2 underline-offset-4 hover:text-lime-700"
                      >
                        Mais de 5.000 m²? Introduzir manualmente
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        id="q-area-manual"
                        data-testid="quote-form-area-manual-input"
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={manualArea}
                        onChange={(e) => setManualArea(e.target.value)}
                        placeholder="Ex.: 12000"
                        className={inputCls}
                      />
                      <button
                        type="button"
                        data-testid="quote-form-area-slider-toggle"
                        onClick={() => setManualArea(null)}
                        className="mt-2 text-xs font-semibold text-pine underline decoration-lime-500 decoration-2 underline-offset-4 hover:text-lime-700"
                      >
                        Voltar ao seletor
                      </button>
                    </>
                  )}
                </div>
                <div>
                  <label htmlFor="q-desc" className={labelCls}>Descrição do espaço / trabalho</label>
                  <textarea
                    id="q-desc"
                    data-testid="quote-form-description-input"
                    rows={4}
                    value={form.descricao}
                    onChange={set("descricao")}
                    placeholder="Ex.: quintal com cerca de 200 m² com mato alto e duas sebes para aparar…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <div>
                  <span className={labelCls}>Fotografias (opcional, máx. 5)</span>
                  <button
                    type="button"
                    data-testid="quote-form-photo-upload"
                    onClick={() => fileInput.current?.click()}
                    className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-stone2 bg-ivory/60 px-4 py-6 text-sm text-moss transition-colors hover:border-lime-400 hover:text-pine"
                  >
                    <ImagePlus size={22} className="text-lime-600" />
                    Adicionar fotografias do espaço
                  </button>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  {files.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {files.map((f, i) => (
                        <div key={i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-stone2">
                          <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                          <button
                            type="button"
                            data-testid={`quote-form-photo-remove-${i}`}
                            onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
                            className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-pine-deep/80 text-ivory"
                            aria-label="Remover fotografia"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  data-testid="quote-form-submit-button"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-1 flex items-center justify-center gap-2 rounded-full bg-pine py-4 text-base font-semibold text-ivory transition-colors duration-200 hover:bg-pine-hover disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> A enviar…
                    </>
                  ) : (
                    "Enviar pedido de orçamento"
                  )}
                </button>
                <p className="text-center text-xs text-moss/70">
                  Os seus dados servem apenas para respondermos ao seu pedido.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
